import React, { useMemo } from 'react';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import DirectRecommendations from 'src/components/profile/recommendations/DirectRecommendations';
import RecommendedCoworkers from 'src/components/profile/recommendations/RecommendedCoworkers';
import type { ViewTypes } from 'src/components/profile/recommendations/RecommendationsModal';

import type { Recommendation, CompanyProfileQuery } from 'src/types/__generated__/graphql';
import { CompanyLabel, RecommendationStatus } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import useLoginModal from 'src/hooks/useLoginModal';
import { isCompanyLabelAllowed } from 'src/utils/companyUtils';
import RecommendedGroupCompanies from 'src/components/profile/recommendations/RecommendedGroupCompanies';
import { styledComponent } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 40rem;
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

const StyledRecommendationBox = styledComponent(Box)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    box-shadow: 0 0.5rem 2.5rem rgb(19 46 72 / 16%);
    margin: 0 -1.5rem;
    background-color: ${colours.white.main};
    flex: 1;
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

interface Props {
    company: NonNullable<CompanyProfileQuery['companyProfile']>;
    handleNext: () => void;
    recommendationFromMe?: Pick<Recommendation, 'id' | 'status'> | null;
    viewType: ViewTypes;
    onSeeAllCompanyRecommendations: () => void;
    onSeeAllRecommendedCoworkers: () => void;
    onSeeAllRecommendedGroupCompanies: () => void;
}

const RecommendationsModalCompanyContent: React.FC<Props> = ({
    company,
    handleNext,
    recommendationFromMe,
    viewType,
    onSeeAllCompanyRecommendations,
    onSeeAllRecommendedCoworkers,
    onSeeAllRecommendedGroupCompanies,
}) => {
    const { t } = useTranslation(['profile', 'common']);
    const { createAuthenticatedHandler } = useLoginModal();

    const isAlreadyRecommended = recommendationFromMe?.status === RecommendationStatus.Accepted;
    const isGroupCompany = isCompanyLabelAllowed(CompanyLabel.Group, company);

    const content = useMemo(() => {
        if (viewType === 'companyRecommendations') {
            return <DirectRecommendations company={company} limit={10} allowFetchMore />;
        }
        if (viewType === 'recommendedCoworkers') {
            return <RecommendedCoworkers company={company} limit={6} allowFetchMore />;
        }
        if (viewType === 'recommendedGroupCompanies') {
            return <RecommendedGroupCompanies company={company} limit={6} allowFetchMore />;
        }
        return (
            <Box marginTop="1.75rem" display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column" height="25rem" mx={-1} overflow="auto">
                    {company.recommendationsReceivedCount > 0 && (
                        <DirectRecommendations
                            company={company}
                            limit={3}
                            allowFetchMore={false}
                            onSeeAllClick={onSeeAllCompanyRecommendations}
                        />
                    )}
                    {company.membersRecommendationsCount > 0 &&
                        (isGroupCompany ? (
                            <RecommendedGroupCompanies
                                company={company}
                                limit={3}
                                allowFetchMore={false}
                                onSeeAllClick={onSeeAllRecommendedGroupCompanies}
                            />
                        ) : (
                            <RecommendedCoworkers
                                company={company}
                                limit={4}
                                allowFetchMore={false}
                                onSeeAllClick={onSeeAllRecommendedCoworkers}
                            />
                        ))}
                </Box>
            </Box>
        );
    }, [
        company,
        isGroupCompany,
        onSeeAllCompanyRecommendations,
        onSeeAllRecommendedCoworkers,
        onSeeAllRecommendedGroupCompanies,
        viewType,
    ]);

    return (
        <>
            {company.companyAndMembersRecommendationsCount === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    height="25rem"
                    justifyContent="center"
                    alignItems="center"
                    flex={1}
                >
                    <Icon color={colours.primary.main} size="7.5rem" name="BigThumbsUp" />
                    <Box maxWidth="10rem" mt={2}>
                        <Typography variant="body2" align="center">
                            {t('profile:recommendationsEmptyState', { fullName: company.name })}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                content
            )}
            <StyledRecommendationBox>
                <Box display="flex" padding="1rem 0" width="100%">
                    <Button
                        sx={{ margin: '0 1.5rem' }}
                        startIcon={<Icon name={isAlreadyRecommended ? 'ThumbsUpCrossed' : 'ThumbsUpOutlined'} />}
                        onClick={
                            process.env.NODE_ENV === 'test' ? handleNext : () => createAuthenticatedHandler(handleNext)
                        }
                        variant={isAlreadyRecommended ? 'outlined' : undefined}
                        fullWidth
                    >
                        {t(isAlreadyRecommended ? 'profile:removeRecommendation' : 'profile:recommend')}
                    </Button>
                </Box>
            </StyledRecommendationBox>
        </>
    );
};

export default RecommendationsModalCompanyContent;
